<template>
  <section class="bg-gray-100">
    <div class="mx-auto flex max-w-7xl flex-col gap-12 px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
      <!-- Header -->
      <div class="mx-auto flex w-full max-w-[720px] flex-col items-center gap-3 text-center">
        <span class="text-sm font-semibold uppercase tracking-wider text-primary-600">From the platforms</span>
        <h2 class="text-pretty text-3xl font-semibold tracking-[-0.4px] text-gray-950 sm:text-4xl sm:tracking-[-0.72px] lg:text-balance">
          What the teams embedding it say
        </h2>
        <p class="text-pretty text-lg text-gray-700">
          Three platforms run the editor in production. Their words go here once they've signed off on them.
        </p>
      </div>

      <!-- Testimonial grid -->
      <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <!-- Featured: DirectMailManager -->
        <figure
          class="sm:col-span-2 flex flex-col gap-6 rounded-2xl border bg-white p-8 shadow-[0px_4px_6px_-2px_rgba(16,24,40,0.06)]"
          style="border-color: transparent; background: linear-gradient(white, white) padding-box, linear-gradient(135deg, var(--primary-300), var(--primary-100) 60%, var(--primary-50)) border-box;"
        >
          <!-- Top row -->
          <div class="flex flex-wrap items-center justify-between gap-4">
            <div class="flex items-center gap-2">
              <img :src="DmmLogo" alt="Direct Mail Manager" class="h-8 w-auto" />
              <span class="text-base font-semibold text-gray-950">DirectMailManager.com</span>
            </div>
            <span class="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700">
              Quote to be supplied
            </span>
          </div>

          <!-- Quote + metric grid -->
          <div class="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <blockquote class="lg:col-span-2">
              <p class="text-[12px] font-bold text-primary-200 leading-none mb-2" aria-hidden="true">"</p>
              <p class="text-xl font-medium leading-relaxed text-gray-950">
                One or two sentences from DirectMailManager.com about the outcome. The best quotes name a before and
                after: what the template flow was, what it is now.
              </p>
            </blockquote>
            <div class="flex flex-col gap-3">
              <div class="rounded-xl border border-dashed border-primary-200 bg-primary-50 p-4">
                <p class="text-3xl font-semibold text-primary-700">—</p>
                <p class="mt-1 text-sm text-gray-700">Headline metric to supply: days to launch, exports a month, or support tickets down</p>
              </div>
            </div>
          </div>

          <!-- Attribution -->
          <figcaption class="flex items-center gap-3 border-t border-gray-300 pt-5">
            <span
              class="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-semibold text-primary-700"
              aria-hidden="true"
            >DM</span>
            <div>
              <p class="text-sm font-semibold text-gray-950">Name</p>
              <p class="text-sm text-gray-700">Role, DirectMailManager.com</p>
            </div>
            <div class="ml-auto flex gap-0.5" aria-label="5 star rating (placeholder)">
              <svg v-for="n in 5" :key="n" class="size-4 text-[#F79009]" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 2l3 6.5 7 .8-5.2 4.8 1.4 7L12 17.6 5.8 21l1.4-7L2 9.3l7-.8z"/>
              </svg>
            </div>
          </figcaption>
        </figure>

        <!-- Mailstream -->
        <figure class="flex flex-col gap-5 rounded-2xl border border-gray-300 bg-white p-6 shadow-[0px_1px_3px_0px_rgba(16,24,40,0.06)]">
          <div class="flex flex-wrap items-center justify-between gap-3">
            <div class="flex items-center gap-2">
              <span class="flex size-8 items-center justify-center rounded-lg bg-success-50 text-xs font-bold text-success-600">MS</span>
              <span class="text-sm font-semibold text-gray-950">Mailstream.app</span>
            </div>
            <span class="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700">
              Quote to be supplied
            </span>
          </div>
          <blockquote>
            <p class="text-sm font-bold text-primary-200 leading-none mb-1" aria-hidden="true">"</p>
            <p class="text-base font-medium text-gray-950">
              One or two sentences from Mailstream.app. Concrete beats glowing: "we replaced our template form with the
              editor in a week".
            </p>
          </blockquote>
          <figcaption class="mt-auto flex items-center gap-3 border-t border-gray-300 pt-4">
            <span
              class="flex size-8 shrink-0 items-center justify-center rounded-full bg-success-50 text-xs font-semibold text-success-600"
              aria-hidden="true"
            >MS</span>
            <div>
              <p class="text-sm font-semibold text-gray-950">Name</p>
              <p class="text-xs text-gray-700">Role, Mailstream.app</p>
            </div>
          </figcaption>
        </figure>

        <!-- Mailflow -->
        <figure class="flex flex-col gap-5 rounded-2xl border border-gray-300 bg-white p-6 shadow-[0px_1px_3px_0px_rgba(16,24,40,0.06)]">
          <div class="flex flex-wrap items-center justify-between gap-3">
            <div class="flex items-center gap-2">
              <span class="flex size-8 items-center justify-center rounded-lg bg-[#eff8ff] text-xs font-bold text-[#175cd3]">MF</span>
              <span class="text-sm font-semibold text-gray-950">Mailflow.app</span>
            </div>
            <span class="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700">
              Quote to be supplied
            </span>
          </div>
          <blockquote>
            <p class="text-sm font-bold text-primary-200 leading-none mb-1" aria-hidden="true">"</p>
            <p class="text-base font-medium text-gray-950">
              One or two sentences from Mailflow.app. If a number is available, lead with it and we move it into the
              metric slot.
            </p>
          </blockquote>
          <figcaption class="mt-auto flex items-center gap-3 border-t border-gray-300 pt-4">
            <span
              class="flex size-8 shrink-0 items-center justify-center rounded-full bg-[#eff8ff] text-xs font-semibold text-[#175cd3]"
              aria-hidden="true"
            >MF</span>
            <div>
              <p class="text-sm font-semibold text-gray-950">Name</p>
              <p class="text-xs text-gray-700">Role, Mailflow.app</p>
            </div>
          </figcaption>
        </figure>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import DmmLogo from "@/../images/integration-icons/dmm-logo.svg";
</script>
